import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Stack,
  Alert,
  Tab,
  Tabs,
  LinearProgress,
  Tooltip,
  // Badge,
  // Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  // Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Engineering as WorkerIcon,
  Queue as QueueIcon,
  Speed as SpeedIcon,
  // Memory as MemoryIcon
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';
import { format } from 'date-fns';

interface Task {
  task_id: string;
  type: string;
  status: string;
  priority: number;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  worker_id?: string;
  attempts: number;
  metadata?: any;
  result?: any;
  error?: string;
}

interface Worker {
  worker_id: string;
  status: string;
  last_heartbeat: string;
  current_task: string;
  tasks_completed: number;
  tasks_failed: number;
  uptime?: string;
  memory_usage?: string;
}

interface QueueStats {
  name: string;
  length: number;
  processing_rate?: number;
  avg_processing_time?: number;
}

interface SystemStats {
  total_tasks: number;
  pending_tasks: number;
  processing_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
  active_workers: number;
  total_workers: number;
  queues: QueueStats[];
}

const OptimizationTasksPage: React.FC = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, _] = useState(5000);

  useEffect(() => {
    loadData();
    
    if (autoRefresh) {
      const interval = setInterval(loadData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load tasks
      const tasksResponse = await axiosInstance.get(`/api/v1/task-queue/tasks`, {
        params: { limit: 100 }
      });
      setTasks(tasksResponse.data);
      
      // Load workers
      const workersResponse = await axiosInstance.get(`/api/v1/task-queue/workers`);
      setWorkers(workersResponse.data);
      
      // Load system stats
      const statsResponse = await axiosInstance.get(`/api/v1/task-queue/stats`);
      setSystemStats(statsResponse.data);
      
      setError(null);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.detail || 'Failed to load optimization data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to cancel this task?')) return;
    
    try {
      await axiosInstance.post(`/api/v1/task-queue/cancel/${taskId}`);
      loadData();
    } catch (err: any) {
      alert(`Failed to cancel task: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleRetryTask = async (taskId: string) => {
    try {
      await axiosInstance.post(`/api/v1/task-queue/retry/${taskId}`);
      loadData();
    } catch (err: any) {
      alert(`Failed to retry task: ${err.response?.data?.detail || err.message}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon color="action" />;
      case 'processing':
        return <CircularProgress size={20} />;
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'cancelled':
        return <CancelIcon color="disabled" />;
      default:
        return <QueueIcon />;
    }
  };

  const getStatusColor = (status: string): any => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'processing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return t(`optimizationTasks.${status}`);
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 0:
        return { label: 'Low', color: 'default' as any };
      case 1:
        return { label: 'Normal', color: 'info' as any };
      case 2:
        return { label: 'High', color: 'warning' as any };
      case 3:
        return { label: 'Urgent', color: 'error' as any };
      default:
        return { label: 'Unknown', color: 'default' as any };
    }
  };

  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const duration = endTime - startTime;
    
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const renderSystemOverview = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('optimizationTasks.totalTasks')}
                </Typography>
                <Typography variant="h4">
                  {systemStats?.total_tasks || 0}
                </Typography>
              </Box>
              <QueueIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
      
      <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('optimizationTasks.activeWorkers')}
                </Typography>
                <Typography variant="h4">
                  {systemStats?.active_workers || 0} / {systemStats?.total_workers || 0}
                </Typography>
              </Box>
              <WorkerIcon sx={{ fontSize: 40, color: 'success.main' }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
      
      <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('optimizationTasks.processing')}
                </Typography>
                <Typography variant="h4">
                  {systemStats?.processing_tasks || 0}
                </Typography>
              </Box>
              <SpeedIcon sx={{ fontSize: 40, color: 'info.main' }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
      
      <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  {t('optimizationTasks.successRate')}
                </Typography>
                <Typography variant="h4">
                  {systemStats && systemStats.total_tasks > 0
                    ? Math.round((systemStats.completed_tasks / systemStats.total_tasks) * 100)
                    : 0}%
                </Typography>
              </Box>
              <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  const renderQueues = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('optimizationTasks.queueStatus')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {systemStats?.queues.map((queue) => (
            <Box key={queue.name} sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {queue.name.toUpperCase()}
                  </Typography>
                  <Typography variant="h5">
                    {queue.length}
                  </Typography>
                  {queue.processing_rate && (
                    <Typography variant="caption" color="text.secondary">
                      {queue.processing_rate.toFixed(1)} tasks/min
                    </Typography>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((queue.length / 100) * 100, 100)}
                    color={queue.name === 'urgent' ? 'error' : 'primary'}
                  />
                </Stack>
              </Paper>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderTasksTable = () => {
    const filteredTasks = activeTab === 0 
      ? tasks 
      : tasks.filter(task => {
          switch (activeTab) {
            case 1: return task.status === 'pending';
            case 2: return task.status === 'processing';
            case 3: return task.status === 'completed';
            case 4: return task.status === 'failed';
            default: return true;
          }
        });

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('optimizationTasks.name')}</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>{t('optimizationTasks.status')}</TableCell>
              <TableCell>{t('optimizationTasks.priority')}</TableCell>
              <TableCell>Worker</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">{t('optimizationTasks.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map((task) => (
              <TableRow key={task.task_id} hover>
                <TableCell>
                  <Tooltip title={task.task_id}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {task.task_id.substring(0, 8)}...
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{task.type}</TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(task.status)}
                    label={getStatusLabel(task.status)}
                    size="small"
                    color={getStatusColor(task.status)}
                  />
                </TableCell>
                <TableCell>
                  {(() => {
                    const priority = getPriorityLabel(task.priority);
                    return (
                      <Chip
                        label={priority.label}
                        size="small"
                        color={priority.color}
                        variant="outlined"
                      />
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {task.worker_id ? (
                    <Tooltip title={task.worker_id}>
                      <Typography variant="body2">
                        {task.worker_id.split('-')[1] || 'Worker'}
                      </Typography>
                    </Tooltip>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {task.started_at ? formatDuration(task.started_at, task.completed_at || task.failed_at) : '-'}
                </TableCell>
                <TableCell>
                  <Tooltip title={format(new Date(task.created_at), 'PPpp')}>
                    <Typography variant="body2">
                      {format(new Date(task.created_at), 'MM/dd HH:mm')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={() => setSelectedTask(task)}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {task.status === 'pending' && (
                      <Tooltip title="Cancel task">
                        <IconButton size="small" onClick={() => handleCancelTask(task.task_id)}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {task.status === 'failed' && (
                      <Tooltip title="Retry task">
                        <IconButton size="small" onClick={() => handleRetryTask(task.task_id)}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderWorkers = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('optimizationTasks.workers')}
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Worker ID</TableCell>
                <TableCell>{t('optimizationTasks.status')}</TableCell>
                <TableCell>{t('optimizationTasks.currentTask')}</TableCell>
                <TableCell>{t('optimizationTasks.tasksCompleted')}</TableCell>
                <TableCell>{t('optimizationTasks.tasksFailed')}</TableCell>
                <TableCell>Last Heartbeat</TableCell>
                <TableCell>Uptime</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workers.map((worker) => (
                <TableRow key={worker.worker_id}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WorkerIcon fontSize="small" />
                      <Typography variant="body2">
                        {worker.worker_id.split('-').slice(1, 3).join('-') || worker.worker_id}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(worker.status)}
                      size="small"
                      color={worker.status === 'processing' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {worker.current_task !== 'none' ? (
                      <Tooltip title={worker.current_task}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {worker.current_task.substring(0, 8)}...
                        </Typography>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={worker.tasks_completed} size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    {worker.tasks_failed > 0 ? (
                      <Chip label={worker.tasks_failed} size="small" color="error" />
                    ) : (
                      <Chip label="0" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(worker.last_heartbeat), 'HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>{worker.uptime || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4">{t('optimizationTasks.title')}</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={autoRefresh ? <PauseIcon /> : <PlayIcon />}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? t('optimizationTasks.pauseAutoRefresh') : t('optimizationTasks.resumeAutoRefresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
          >
            {t('optimizationTasks.refresh')}
          </Button>
        </Stack>
      </Stack>

      {_error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {_error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Stack spacing={3}>
        {/* System Overview */}
        {renderSystemOverview()}
        
        {/* Queue Status */}
        {renderQueues()}
        
        {/* Tasks Table */}
        <Card>
          <CardContent>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab label={`${t('optimizationTasks.all')} (${tasks.length})`} />
              <Tab label={`${t('optimizationTasks.pending')} (${tasks.filter(t => t.status === 'pending').length})`} />
              <Tab label={`${t('optimizationTasks.processing')} (${tasks.filter(t => t.status === 'processing').length})`} />
              <Tab label={`${t('optimizationTasks.completed')} (${tasks.filter(t => t.status === 'completed').length})`} />
              <Tab label={`${t('optimizationTasks.failed')} (${tasks.filter(t => t.status === 'failed').length})`} />
            </Tabs>
            {renderTasksTable()}
          </CardContent>
        </Card>
        
        {/* Workers */}
        {renderWorkers()}
      </Stack>

      {/* Task Details Dialog */}
      <Dialog open={!!selectedTask} onClose={() => setSelectedTask(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('optimizationTasks.taskDetails')}</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Stack spacing={2}>
              <TextField
                label="Task ID"
                value={selectedTask.task_id}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Type"
                value={selectedTask.type}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Status"
                value={selectedTask.status}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Priority"
                value={getPriorityLabel(selectedTask.priority).label}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              {selectedTask.metadata && (
                <TextField
                  label="Metadata"
                  value={JSON.stringify(selectedTask.metadata, null, 2)}
                  fullWidth
                  multiline
                  rows={4}
                  InputProps={{ readOnly: true }}
                />
              )}
              {selectedTask.error && (
                <TextField
                  label="Error"
                  value={selectedTask.error}
                  fullWidth
                  multiline
                  rows={4}
                  InputProps={{ readOnly: true }}
                  error
                />
              )}
              {selectedTask.result && (
                <TextField
                  label="Result"
                  value={JSON.stringify(selectedTask.result, null, 2)}
                  fullWidth
                  multiline
                  rows={6}
                  InputProps={{ readOnly: true }}
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTask(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OptimizationTasksPage;